import React, { useState } from 'react';
import {
    Stack,
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    Link,
    InputAdornment,
    Select,
    MenuItem
} from '@mui/material';
import { register, verifyPhone, sendVerificationCode } from '../../utils/api';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import MailIcon from '@mui/icons-material/Mail';
import GoogleIcon from '@mui/icons-material/Google';
import PhoneIcon from '@mui/icons-material/Phone';
import emailjs from '@emailjs/browser';

const Register = ({ open, onClose, onLoginClick }) => {
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        email: '',
        role: 'trainee',
        code: '',
        phone: '',
        googleId: null, // Add googleId to formData
    });
    const [errors, setErrors] = useState({
        username: '',
        password: '',
        email: '',
        code: '',
        phone: '',
    });
    const [isVerifying, setIsVerifying] = useState(false);
    const [userId, setUserId] = useState(null);
    const [verificationCode, setVerificationCode] = useState(''); // Phone verification code
    const [emailCode, setEmailCode] = useState(''); // Email verification code
    const [verificationError, setVerificationError] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');

    const validateForm = () => {
        let isValid = true;
        let errors = { username: '', password: '', email: '', code: '', phone: '' };

        const passwordRegex = /^(?=.*[A-Z])(?=.*\d).{8,}$/;
        if (!passwordRegex.test(formData.password)) {
            errors.password = 'Password must be at least 8 characters, include a capital letter and a number';
            isValid = false;
        }

        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.(com)$/;
        if (!emailRegex.test(formData.email)) {
            errors.email = 'Email must be a valid email with "@domain.com"';
            isValid = false;
        }

        if (!formData.code) {
            errors.code = 'Code is required';
            isValid = false;
        }

        if (!formData.phone) {
            errors.phone = 'Phone number is required';
            isValid = false;
        } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone)) {
            errors.phone = 'Invalid phone number format (e.g., +1234567890)';
            isValid = false;
        }

        setErrors(errors);
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const response = await register(formData);
            if (response && response.data) {
                setUserId(response.data.userId);

                // Generate a 6-digit email verification code
                const emailVerificationCode = Math.floor(100000 + Math.random() * 900000).toString();

                // Send the email verification code using EmailJS
                const templateParams = {
                    name: formData.username,
                    email: formData.email,
                    phone: formData.phone || 'Not provided',
                    message: "Hello, Here is your email verification code " + emailVerificationCode,
                    time: new Date().toLocaleString(),
                };

                try {
                    await emailjs.send(
                        'service_46bv0oq', // Your EmailJS service ID (same as ForgotPassword)
                        'template_qedmi1r', // Your custom EmailJS template ID for email verification
                        templateParams,
                        'yH0mz21D4OHFGHtEf' // Your EmailJS user ID (same as ForgotPassword)
                    );
                    // Store the email verification code in localStorage

                    localStorage.setItem('emailVerificationCode', emailVerificationCode);

                } catch (error) {
                    console.error('Failed to send verification email:', error);
                    setErrors((prev) => ({
                        ...prev,
                        email: 'Failed to send verification email. Please try again.',
                    }));
                    return;
                }
                setPhoneNumber(formData.phone);

                // Send phone verification code (unchanged)
                await sendVerificationCode(response.data.userId, formData.phone);
                localStorage.setItem('phone', formData.phone); // Store the phone number in localStorage
                setIsVerifying(true);
            } else {
                console.error('No data returned from the register API');
            }
        } catch (error) {
            console.error('Registration error:', error.response?.data || error.message);
            if (error.response && error.response.status === 429) {
                setErrors((prev) => ({
                    ...prev,
                    code: error.response.data.message || 'Too many attempts. Please try again later.',
                }));
            } else {
                setErrors((prev) => ({
                    ...prev,
                    code: error.response?.data?.message || 'Registration failed',
                }));
            }
        }
    };

    const handleVerify = async () => {
        // Retrieve the stored email verification code from localStorage
        const storedEmailCode = localStorage.getItem('emailVerificationCode');

        // Verify the email code client-side
        if (emailCode !== storedEmailCode) {
            setVerificationError('Invalid email verification code');
            return;
        }

        // Verify the phone code server-side (unchanged)
        try {
            const response = await verifyPhone(userId, verificationCode, localStorage.getItem('phone'));
            if (response.message === 'Phone verified successfully') {
                // Remove the email verification code from localStorage after successful verification
                localStorage.removeItem('emailVerificationCode');
                window.location.reload();
            } else {
                setVerificationError('Invalid phone verification code');
            }
        } catch (error) {
            setVerificationError('Failed to verify phone');
        }
    };

    if (isVerifying) {
        return (
            <Modal open={open} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '10px',
                }}>
                    <Typography textAlign="right" direction="rtl" fontFamily="GveretLevin" color="#B5A78A" variant="h6">לצורך אימות  </Typography>
                    <Typography textAlign="right" direction="rtl" fontFamily="GveretLevin" > יש להזין את המספרי קוד שנשלחו לנייד ומייל.</Typography>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="קוד לטלפון"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        required
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="קוד למייל"
                        value={emailCode}
                        onChange={(e) => setEmailCode(e.target.value)}
                        required
                    />
                    {verificationError && <Typography color="error">{verificationError}</Typography>}
                    <Button fullWidth variant="contained" onClick={handleVerify} sx={{ mt: 2 }}>
                        אימות
                    </Button>
                </Box>
            </Modal>
        );
    }

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 400,
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                borderRadius: '10px',
            }}>
                <Typography sx={{
                    fontFamily: 'GveretLevin',

                }}
                    textAlign="right"
                    direction="rtl"
                    color="#B5A78A"
                    variant="h6" mb={2}>רישום לאפלקציה</Typography>
                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="מייל"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        error={!!errors.email}
                        helperText={errors.email}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <MailIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="משתמש"
                        name="username"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        required
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        type="password"
                        label="סיסמה"
                        name="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        error={!!errors.password}
                        helperText={errors.password}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Select
                        fullWidth
                        margin="normal"
                        label="Role"
                        name="role"
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        required
                    >
                        <MenuItem value="trainee">מתאמן</MenuItem>
                        <MenuItem value="trainer">מאמן</MenuItem>
                    </Select>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="קוד רישום"
                        name="code"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                        required
                        error={!!errors.code}
                        helperText={errors.code}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="מספר טלפון"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        error={!!errors.phone}
                        helperText={errors.phone}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PhoneIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{
                            backgroundColor: '#C4A484',
                            '&:hover': { backgroundColor: '#C4A484' },
                            mt: 2,
                        }}                    >
                        רישום
                    </Button>
                </form>
                <Typography textAlign="right" direction="rtl" variant="body2" sx={{ mt: 2 }}>
                    משתמש קיים?{' '}
                    <Link href="#" onClick={(e) => { e.preventDefault(); onClose(); onLoginClick(); }}>
                        התחברות
                    </Link>
                </Typography>
                <Box textAlign="center" mt={2}>
                    <Typography variant="body2">Or</Typography>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mt={1}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                window.location.href = 'https://pilatesnflow.onrender.com/auth/google';//'http://localhost:5000/auth/google';// 
                            }}
                            sx={{
                                textTransform: 'none',
                                color: '#4285F4',
                                borderColor: '#4285F4',
                                '&:hover': { borderColor: '#4285F4', backgroundColor: '#F1F1F1' },
                                padding: '10px 20px',
                            }}
                        >
                            <GoogleIcon sx={{ mr: 1 }} />
                            התחברות ורישום  עם חשבוון גוגל
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Modal >
    );
};

export default Register;